import React, { useState } from 'react';
import { Hand, MessageCircle, X, BadgeHelp, CheckCircle2 } from 'lucide-react';

export default function PublicWhatsAppSupportButton({ whatsappNumber = '', enabled = false, message = '' }) {
  const normalizedNumber = String(whatsappNumber || '').replace(/\D/g, '');
  const hasCountryCode = normalizedNumber.startsWith('55')
    && (normalizedNumber.length === 12 || normalizedNumber.length === 13);
  const fullNumber = hasCountryCode ? normalizedNumber : `55${normalizedNumber}`;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    if (!enabled || !normalizedNumber) return;
    setIsModalOpen(true);
  };

  const handleOpenWhatsApp = () => {
    if (!enabled || !normalizedNumber) return;
    const fallbackMessage = 'Olá! Preciso de suporte na plataforma SOFERA.';
    const text = String(message || fallbackMessage).trim() || fallbackMessage;
    const whatsappUrl = `https://wa.me/${fullNumber}?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setIsModalOpen(false);
  };

  if (!enabled || !normalizedNumber) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleOpenModal}
        className="fixed bottom-[92px] right-4 sm:bottom-[88px] md:bottom-[84px] md:right-6 z-[140] max-w-[calc(100vw-2rem)] flex items-center gap-2 md:gap-3 rounded-full bg-green-600 hover:bg-green-500 text-white shadow-xl transition hover:scale-[1.02] pl-2.5 pr-3 py-2.5 md:pl-3 md:pr-4 md:py-3"
        title="Precisa de suporte?"
        aria-label="Precisa de suporte? Falar no WhatsApp"
      >
        <span className="relative flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-full bg-white/12 border border-white/15">
          <span className="absolute inset-0 rounded-full bg-white/10 motion-safe:animate-ping opacity-75"></span>
          <span className="relative z-10">
            <MessageCircle size={18} className="md:w-5 md:h-5" />
          </span>
        </span>
        <span className="flex items-center gap-1.5 md:gap-2 whitespace-nowrap text-xs md:text-sm font-black">
          <Hand size={14} className="md:w-4 md:h-4" />
          <span className="hidden min-[380px]:inline">Precisa de suporte?</span>
          <span className="min-[380px]:hidden">Suporte?</span>
        </span>
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-500/10 border border-green-500/20 text-green-400">
                  <BadgeHelp size={22} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Suporte via WhatsApp</h3>
                  <p className="mt-1 text-sm text-slate-400">Orientações para agilizar o atendimento.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-slate-500 hover:text-white transition"
                aria-label="Fechar modal de suporte"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-slate-300 leading-relaxed">
              Você será direcionado para o suporte da <span className="font-semibold text-white">A2Care</span>, desenvolvedora da plataforma.
            </p>
            <p className="mt-3 text-slate-300 leading-relaxed">
              Para um atendimento mais ágil e efetivo, informe no WhatsApp:
            </p>

            <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/70 p-4 space-y-3">
              <div className="flex items-center gap-3 text-sm text-slate-200">
                <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                <span>Nome Completo</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-200">
                <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                <span>Empresa</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-200">
                <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                <span>Qual o problema ou necessidade</span>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Depois de abrir o WhatsApp, descreva sua situação com o máximo de detalhes para acelerar o suporte.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-lg text-slate-300 hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleOpenWhatsApp}
                className="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-bold transition"
              >
                Continuar para o WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
